'use client';

/**
 * SearchResultsSection — schema-driven entity search result list.
 *
 * Shows up after a narrative search (`ki_ask`) returns multiple
 * matches and the user needs to pick one. Each row dispatches via
 * `context.selectResult(result)`; the provider decides what
 * "select" means for that mode (obeya: run a simulation; agents:
 * focus the entity; future: open in tab, etc.).
 *
 * Reads `searchResults` from `<ExplorerSearchProvider />`; renders
 * nothing when the list is empty.
 *
 * Honors `config.max_items` — backends that want to cap the visible
 * result count without changing UI code declare it in the schema.
 *
 * @see klyde_edge/ui_config/entity_schema.py — ExplorerSectionType.SEARCH_RESULTS
 *
 * @component_role: Section
 * @component_type: Section Renderer
 * @domain: Frontend / Explorer
 * @layer: presentation
 */

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import type { ExplorerSectionConfig, LabelsDict } from '@/lib/api/types';
import { useExplorerSearch } from './explorer-search-context';

interface SearchResultsSectionProps {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}

export function SearchResultsSection({ config, labels }: SearchResultsSectionProps) {
  const search = useExplorerSearch();
  if (!search || search.searchResults.length === 0) return null;

  const results = config.max_items
    ? search.searchResults.slice(0, config.max_items)
    : search.searchResults;

  // Schema label is the English key; labels dict maps it to the active
  // language. Falls through to the schema literal when the dict
  // doesn't carry the key (e.g. mode hasn't translated it yet).
  const heading =
    (config.label && labels[config.label])
    ?? labels.select_entity
    ?? 'Select an entity';

  return (
    <div className="border-b border-border">
      <div className="px-3 py-2 text-xs font-semibold text-muted-foreground">
        {heading}
      </div>
      <div className="space-y-1 px-2 pb-2">
        {results.map((result) => (
          <button
            key={result.semantic_id}
            onClick={() => search.selectResult(result)}
            className={cn(
              'w-full text-left p-2 rounded-md hover:bg-accent/50 transition-colors',
              'border border-transparent hover:border-border',
            )}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">
                {result.name || result.semantic_id}
              </span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {result.entity_type}
              </Badge>
              {result.state && (
                <span className="text-[10px] text-muted-foreground ml-auto">
                  {result.state}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
